import "./utils/easyblocks-log-filter";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AppMode, initializeAppStore, resolveMode, navigateToRender } from "./utils/app-bootstrap";

if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
    const devStylesheet = document.createElement("link");
    devStylesheet.rel = "stylesheet";
    devStylesheet.href = "https://friscolakes.net/css/main_public.css";
    document.head.appendChild(devStylesheet);

    const bootstrapScript = document.createElement("script");
    bootstrapScript.src = "https://friscolakes.net/js/vendor/bootstrap.bundle.min.js";
    document.body.appendChild(bootstrapScript);
}

// Render the app
const mountEl =
    document.getElementById("easyblocks-editor-root") ??
    document.getElementById("easyblocks-render-root") ??
    document.getElementById("root");

if (!mountEl) {
    console.warn("[easyblocks] No mount element found.");
}

// Vite sets `import.meta.env.MODE` from the CLI mode:
// - `vite` -> development
// - `vite build` -> production
// - `vite build --mode editor|render` -> editor/render
const buildTarget = (import.meta as { env?: { MODE?: string } }).env?.MODE ?? "development";
let runtimeMode: AppMode;
if (buildTarget === "editor") {
    runtimeMode = "editor";
} else if (buildTarget === "render") {
    runtimeMode = "render";
} else {
    runtimeMode = resolveMode(mountEl?.id);
}

const hot = (import.meta as { hot?: { data?: { root?: ReturnType<typeof createRoot> } } }).hot;

if (mountEl) {
    const root = hot?.data?.root ?? createRoot(mountEl);
    if (hot?.data) {
        hot.data.root = root;
    }

    void (async () => {
        const { isEmbeddedMount } = await initializeAppStore({
            mountEl,
            runtimeMode,
        });

        // Add class to body for editor mode detection
        if (runtimeMode === "editor") {
            document.body.classList.add("is-easyblocks-editor");
        }

        let app: JSX.Element;
        if (runtimeMode === "editor") {
            const { EditorApp } = await import("./app/editor-app");
            app = (
                <EditorApp onExitToRender={isEmbeddedMount ? undefined : navigateToRender} />
            );
        } else {
            const { RenderApp } = await import("./app/render-app");
            app = <RenderApp />;
        }

        root.render(<StrictMode>{app}</StrictMode>);
    })();
}

/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */
